import { cloneDeep, omit } from "lodash";

import { IDictionaryEntry } from "core/components/label/ILabel";
import initialState from "core/reducers/initialState";

/**
 * helper class for doing common and annoying dictionary entry finding tasks
 *
 * hierarchical structure is:
 *
 * key -> language -> instance -> country (tbd on long term plan for country)
 */

type Dictionary = typeof initialState.dictionary;

interface IDictionaryEntryParams {
	key: string;
	language: string;
	instances?: string[];
}

interface IDictionaryValue {
	value: string;
	type?: string;
}

export interface IDictionaryResult extends IDictionaryValue {
	language: string;
	key: string;
	instance?: string;
}

export default class DictionaryUtil {
	public static DEFAULT_KEY = "__DEFAULT";

	/**
	 * Resolve the most relevant value from hierarchical dictionary data.
	 * @param dictionary 	Dictionary data to scan
	 * @param defaultValue 	value, type
	 * @param key 			label key
	 * @param language 		current user's language
	 * @param country 		current user's country, if available
	 * @param instances 	list of instance overrides to check (optional)
	 * @returns
	 */
	public static resolveValue(dictionary: Dictionary, defaultValue: IDictionaryValue, key: string, language: string, instances?: string[]): IDictionaryResult {
		// MUST provide label key.
		if (!key) {
			throw new Error("Tried to Access a Label Without Specifying a Key!");
		}

		// check to see if any dictionary data is available for this key + language
		if (dictionary[key] && dictionary[key][language]) {
			const keylang = dictionary[key][language];

			// if any instance overrides, check through those
			if (instances && instances.length > 0) {
				for (let i = 0, len = instances.length; i < len; i++) {
					const instanceEntry = keylang[instances[i]];
					if (instanceEntry) {

						// MATCHED: key + language + instance
						return { ...instanceEntry, key, language, instance: instances[i] };
					}
				}
			}

			if (keylang[this.DEFAULT_KEY]) {
				const dv = keylang[this.DEFAULT_KEY];

				return { ...dv, language, key };
			}
		}

		// No Match
		return { ...defaultValue, language, key };
	}

	/**
	 * resolve a group of labels.
	 * @param dictionary
	 * @param defaultValues
	 * @param keys
	 * @param language
	 * @param instances
	 */
	public static pick(dictionary: Dictionary, labels: { [key: string]: string; }, language: string, instances?: string[]): { [key: string]: IDictionaryResult } {
		return Object.keys(labels).reduce((a, key) => {

			const resolved = this.resolveValue(dictionary, { value: labels[key] }, key, language, instances);

			if (resolved) {
				a[key] = resolved;
			}

			return a;
		}, {});
	}

	/**
	 * same as pick, but map to just values.
	 * @param dictionary
	 * @param defaultValues
	 * @param keys
	 * @param language
	 * @param instances
	 */
	public static pickValues(dictionary: Dictionary, labels: { [key: string]: string; }, language: string, instances?: string[]): { [key: string]: string } {
		return Object.entries(this.pick(dictionary, labels, language, instances)).reduce((a, [key, e]) => {

			a[key] = e.value;

			return a;
		}, {});
	}

	/**
	 * Get a specific value from dictionary data, no priority checks.
	 * @param dictionary
	 * @param key
	 * @param language
	 * @param instance
	 */
	public static getValue(dictionary: Dictionary, key: string, language: string, instance: string = this.DEFAULT_KEY): IDictionaryResult | null {
		const instKey = instance || this.DEFAULT_KEY;
		return (
			dictionary[key] && dictionary[key][language] && dictionary[key][language][instKey] || null
		);
	}

	public static setValue(dictionary: Dictionary, data: IDictionaryValue, key: string, language: string, instance?: string, id?: string) {
		const instKey = instance || this.DEFAULT_KEY;
		if (!dictionary[key]) {
			dictionary[key] = {};
		}
		if (!dictionary[key][language]) {
			dictionary[key][language] = {};
		}
		if (!dictionary[key][language][instKey]) {
			dictionary[key][language][instKey] = {};
		}
		dictionary[key][language][instKey] = data;
	}

	// immutably add/remove values

	public static addValue(dictionary: Dictionary, entry: IDictionaryEntry): Dictionary {
		const copy = cloneDeep(dictionary);

		this.setValue(copy, { value: entry.value, type: entry.type }, entry.key, entry.language, entry.instance, entry.id);

		return copy;
	}

	public static addValues(dictionary: Dictionary, entries: IDictionaryEntry[]): Dictionary {
		const copy = cloneDeep(dictionary);

		entries.forEach((entry) => {
			this.setValue(copy, { value: entry.value, type: entry.type }, entry.key, entry.language, entry.instance, entry.id);
		});

		return copy;
	}

	public static removeValue(dictionary: Dictionary, key: string, language: string, instance?: string): Dictionary {
		const instKey = instance || this.DEFAULT_KEY;

		const copy = cloneDeep(dictionary);

		if (copy[key] && copy[key][language] && copy[key][language][instKey]) {
			copy[key][language] = omit(copy[key][language], instKey);
		}

		return copy;
	}
}



// WEBPACK FOOTER //
// ./src/core/util/dictionary/DictionaryUtil.ts